import {
  ALL_DISTRIBUTORS_ID,
  AnalyticsDateRange,
  OrgBreakdown,
  SalesMetric,
  TimePeriod,
} from "types/analyticsTypes";
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Title,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import {
  clearOrgBreakdowns,
  removeAllDistributorIds,
  requestAllDistributorIds,
  requestCategoryDistribution,
  requestDemoTestTotals,
  requestDistributorComparison,
  requestDistributorOrgTestCounts,
  requestDistributorSales,
  requestMultiAccountDevices,
  setOrgBreakdown,
} from "@store/analytics/analyticsSlice";
import {
  selectAllDistributorIds,
  selectCategoryDistribution,
  selectDemoTestTotals,
  selectDistributorComparison,
  selectDistributorOrgTestCounts,
  selectDistributorSales,
  selectMultiAccountDevices,
  selectOrgBreakdowns,
} from "@store/analytics/analyticsSelectors";
import {
  selectUserDistributorId,
  selectUserOrgId,
  selectUserRole,
} from "@utils/redux-utils/selectors/userSelectors";
import { useDispatch, useSelector } from "react-redux";

import AnalyticsKpiCards from "./AnalyticsKpiCards";
import AnalyticsReport from "./AnalyticsReport";
import CategoryDistributionChart from "./CategoryDistributionChart";
import { DatePickerInput } from "@mantine/dates";
import DistributorComparisonTable from "./DistributorComparisonTable";
import DistributorSalesChart from "./DistributorSalesChart";
import { IconSearch } from "@tabler/icons-react";
import MultiAccountDevicesNotice from "./MultiAccountDevicesNotice";
import OrganizationTestCountsTable from "./OrganizationTestCountsTable";
import dayjs from "dayjs";
import { fetchOrgBreakdown } from "@utils/api-communication/RestApiCli";
import { usePrintable } from "@hooks";
import { navigateToSettingsPage } from "@store/ui/uiSlice";
import { useTranslation } from "react-i18next";
import { parseDateRangeOnly } from "@utils/CommonUtil";

const AnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [distId, setDistId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<AnalyticsDateRange>([null, null]);
  const [salesMetric, setSalesMetric] = useState<SalesMetric>(SalesMetric.ACCOUNTS);
  const [period, setPeriod] = useState<TimePeriod>(TimePeriod.MONTH);
  const [exporting, setExporting] = useState<boolean>(false);
  const { generate, loading: printLoading } = usePrintable();

  const salesData = useSelector(selectDistributorSales);
  const testCounts = useSelector(selectDistributorOrgTestCounts);
  const categoryDistribution = useSelector(selectCategoryDistribution);
  const demoTotals = useSelector(selectDemoTestTotals);
  const distributorComparison = useSelector(selectDistributorComparison);
  const multiAccountDevices = useSelector(selectMultiAccountDevices);
  const userDistId = useSelector(selectUserDistributorId);
  const userOrgId = useSelector(selectUserOrgId);
  const role = useSelector(selectUserRole);
  const allDistributorIds = useSelector(selectAllDistributorIds);
  const orgBreakdowns = useSelector(selectOrgBreakdowns);

  const [rangeStart, rangeEnd] = dateRange;
  const rangeActive = Boolean(rangeStart);
  const startDate = rangeStart ? dayjs(rangeStart).format("YYYY-MM-DD") : undefined;
  const endDate = rangeStart ? dayjs(rangeEnd ?? rangeStart).format("YYYY-MM-DD") : undefined;

  const periodDays: Partial<Record<TimePeriod, number>> = {
    [TimePeriod.WEEK]: 7,
    [TimePeriod.MONTH]: 30,
    [TimePeriod.QUARTER]: 90,
    [TimePeriod.YEAR]: 365,
  };
  const breakdownStartDate = rangeActive
    ? startDate
    : periodDays[period]
      ? dayjs().subtract(periodDays[period], "day").format("YYYY-MM-DD")
      : undefined;
  const breakdownEndDate = rangeActive
    ? endDate
    : periodDays[period]
      ? dayjs().format("YYYY-MM-DD")
      : undefined;

  useEffect(() => {
    if (role === "admin") {
      setDistId(ALL_DISTRIBUTORS_ID);
    } else if (userDistId) {
      setDistId(userDistId);
    } else {
      setDistId(userOrgId);
    }
  }, [role, userDistId, userOrgId]);

  const distributorOptions = [
    { value: ALL_DISTRIBUTORS_ID, label: t("analytics.distributor.selector.all") },
    ...(allDistributorIds ?? []).map((dist) => ({
      value: dist.distId,
      label: dist.country ? `${dist.distId} (${dist.country})` : dist.distId,
    })),
  ];
  const countryByDistId = Object.fromEntries(
    (allDistributorIds ?? []).map((dist) => [dist.distId, dist.country]),
  );
  const showComparison = role === "admin" && distId === ALL_DISTRIBUTORS_ID;

  useEffect(() => {
    if (distId) {
      dispatch(requestDistributorOrgTestCounts({ distributorId: distId, startDate, endDate }));
      dispatch(requestDistributorSales({ distributorId: distId, startDate, endDate }));
      dispatch(requestCategoryDistribution({ distributorId: distId, startDate, endDate }));
    }
  }, [distId, startDate, endDate, dispatch]);

  useEffect(() => {
    if (distId) {
      dispatch(requestDemoTestTotals({ distributorId: distId }));
      dispatch(requestMultiAccountDevices({ distributorId: distId }));
    }
  }, [distId, dispatch]);

  useEffect(() => {
    if (showComparison) {
      dispatch(requestDistributorComparison({ startDate, endDate }));
    }
  }, [showComparison, startDate, endDate, dispatch]);

  useEffect(() => {
    dispatch(clearOrgBreakdowns());
  }, [distId, period, startDate, endDate, dispatch]);

  useEffect(() => {
    if (role === "admin") {
      dispatch(requestAllDistributorIds());
    } else {
      dispatch(removeAllDistributorIds());
    }
  }, [role, distId, dispatch]);

  const handleDistributorChange = (value: string | null) => {
    if (value) {
      setDistId(value);
    }
  };

  const handleOrgClick = (orgId: string) => {
    dispatch(navigateToSettingsPage(orgId));
  };

  const analyticsLoaded = Boolean(testCounts && salesData && categoryDistribution);

  const handleExport = async () => {
    setExporting(true);
    try {
      const missing = (testCounts ?? []).filter((org) => !orgBreakdowns[org.orgId]);
      await Promise.all(
        missing.map(async (org) => {
          try {
            const breakdown = (await fetchOrgBreakdown(
              org.orgId,
              breakdownStartDate,
              breakdownEndDate,
            )) as OrgBreakdown;
            dispatch(setOrgBreakdown({ orgId: org.orgId, breakdown }));
          } catch {
            dispatch(
              setOrgBreakdown({ orgId: org.orgId, breakdown: { devices: [], categories: [] } }),
            );
          }
        }),
      );
      await new Promise((resolve) => setTimeout(resolve, 400));
      await generate("download", {
        filename: `analytics-${distId ?? "report"}-${dayjs().format("YYYY-MM-DD")}.pdf`,
      });
    } catch (error) {
      console.error("Analytics PDF export failed", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Flex justify="space-between" align="center" mb="md" wrap="wrap" gap="md">
        <Title order={2}>{t("TestDashboard.analytics")}</Title>

        <Group>
          <DatePickerInput
            type="range"
            value={dateRange}
            onChange={(v) => setDateRange(parseDateRangeOnly(v))}
            placeholder={t("analytics.dateRange.placeholder")}
            clearable
            allowSingleDateInRange
          />
          {role === "admin" && allDistributorIds && allDistributorIds.length > 0 && (
            <Select
              placeholder={t("analytics.distributor.selector.placeholder")}
              data={distributorOptions}
              value={distId}
              onChange={handleDistributorChange}
              searchable
              clearable={false}
              leftSection={<IconSearch size={14} />}
            />
          )}
          <Button
            size="compact-md"
            onClick={handleExport}
            disabled={exporting || printLoading || !analyticsLoaded}
          >
            {exporting || printLoading ? <Loader color={"white"} size={20} /> : t("export")}
          </Button>
        </Group>
      </Flex>

      <Box>
        <AnalyticsKpiCards
          testCounts={testCounts}
          sales={salesData}
          demoTotals={demoTotals}
          period={rangeActive ? TimePeriod.RANGE : period}
          interactive
        />
        <MultiAccountDevicesNotice data={multiAccountDevices} />
        <Grid gutter="md" mt="md">
          <Grid.Col span={12}>
            <Paper shadow="xs" p="md" withBorder>
              <Flex justify="space-between" align="center" mb="md" wrap="wrap" gap="sm">
                <Title order={3}>{t("analytics.sales.newlyCreatedAccounts.title")}</Title>
                <SegmentedControl
                  value={salesMetric}
                  onChange={(value) => setSalesMetric(value as SalesMetric)}
                  data={[
                    { label: t("analytics.sales.metric.accounts"), value: SalesMetric.ACCOUNTS },
                    { label: t("analytics.sales.metric.devices"), value: SalesMetric.DEVICES },
                  ]}
                />
              </Flex>
              <DistributorSalesChart data={salesData} metric={salesMetric} />
            </Paper>
          </Grid.Col>
          <Grid.Col span={12}>
            <Paper shadow="xs" p="md" withBorder>
              <Title order={3} mb="md">
                {t("analytics.category.title")}
              </Title>
              <CategoryDistributionChart data={categoryDistribution} />
            </Paper>
          </Grid.Col>
          {showComparison && (
            <Grid.Col span={12}>
              <Paper shadow="xs" p="md" withBorder>
                <DistributorComparisonTable
                  data={distributorComparison}
                  countryByDistId={countryByDistId}
                  onDistributorClick={handleDistributorChange}
                />
              </Paper>
            </Grid.Col>
          )}
          <Grid.Col span={12}>
            <Paper shadow="xs" p="md" withBorder>
              <OrganizationTestCountsTable
                data={testCounts}
                period={period}
                onPeriodChange={setPeriod}
                rangeActive={rangeActive}
                startDate={breakdownStartDate}
                endDate={breakdownEndDate}
                onOrgClick={handleOrgClick}
              />
            </Paper>
          </Grid.Col>
        </Grid>
      </Box>
      {exporting && (
        <AnalyticsReport
          period={period}
          rangeActive={rangeActive}
          startDate={rangeActive ? startDate : undefined}
          endDate={rangeActive ? endDate : undefined}
        />
      )}
    </Container>
  );
};

export default AnalyticsDashboard;
